import React, { useEffect } from 'react';
import DynamicServiceFAQ from '@/components/services/DynamicServiceFAQ';
import TopBar from '@/components/atelier/TopBar';
import Nav from '@/components/atelier/Nav';
import Footer from '@/components/atelier/Footer';
import EyebrowPill from '@/components/atelier/EyebrowPill';
import FinalCTACard from '@/components/atelier/FinalCTACard';
import KeyFactsBox from '@/components/atelier/KeyFactsBox';
import { addJsonLd, clearJsonLd, getSiteSchemas, HAIRSALON_SCHEMA, setPageDescription } from '@/lib/seo';

const BOOKING_URL = 'https://www.fresha.com/a/ikonomakis-hair-atelier-halandri-irakleitoy-80-bxynmijf?pId=770996';
const SITE_URL = 'https://ikonomakis.gr';
const PAGE_URL = `${SITE_URL}/en/ai-hair-analysis`;

const DEFINITIVE_STATEMENT = 'Ikonomakis Hair Atelier is the first salon in Greece to offer AI facial analysis as a formal part of its consultation process.';

const FAQS = [
  { question: 'What exactly is Face Metrics Pro?', answer: 'Face Metrics Pro is a structured facial morphology analysis method developed by Thodoris Ikonomakis. It uses your facial geometry, proportions and symmetry to generate personalised recommendations for haircut shape, colour technique and styling direction. It\'s not a general style quiz — it\'s a precise, evidence-based consultation.' },
  { question: 'How is it different from a regular consultation?', answer: 'A regular consultation relies on stylist intuition and client preferences. Face Metrics Pro adds a measurable, repeatable framework: specific facial ratios are calculated, compared to proportional benchmarks, and used to derive objective recommendations. The result is documented, not just discussed.' },
  { question: 'Do I need to book it separately?', answer: 'Yes. Face Metrics Pro is a standalone 45-minute session priced at €100. It can be combined with a hair service on the same day, or booked independently as a standalone consultation. Many clients book it before a major transformation to ensure confidence in the direction.' },
  { question: 'What do I receive at the end?', answer: 'You receive a written summary of your facial analysis, with specific recommendations for cut shape, fringe options, length, colour technique and styling. This document is yours to keep and can be shared with any stylist — including outside our atelier.' },
  { question: 'Is it available in English?', answer: 'Yes. Face Metrics Pro sessions are conducted in Greek or English. Thodoris Ikonomakis, who developed and leads the method, is fluent in English and has worked internationally across 19 countries.' },
];

const PROCESS_STEPS = [
  { n: '01', title: 'Initial photograph & measurement', body: 'A neutral, front-facing photograph is taken under controlled lighting. Key facial landmarks are identified and distances recorded.' },
  { n: '02', title: 'Ratio & geometry analysis', body: 'Facial width-to-length ratio, forehead width, jawline angle and the positioning of cheekbones are calculated. The results place your face within a morphological profile.' },
  { n: '03', title: 'Symmetry assessment', body: 'Left-right facial symmetry is evaluated. Areas of asymmetry are noted — not as flaws, but as factors that hairstyle and colour can optically balance.' },
  { n: '04', title: 'Personalised recommendations', body: 'Based on the data, specific recommendations are generated: cut length and shape, fringe type (or absence), colour technique best suited to your profile, and styling approach.' },
  { n: '05', title: 'Written report', body: 'All findings are documented in a written report that you receive at the end of the session. It includes the measurements, the morphological classification, and the full set of recommendations.' },
];

export default function EnAiHairAnalysisPage() {


  useEffect(() => {
    document.title = 'AI Hair Analysis Athens – Face Metrics Pro | Ikonomakis';
    setPageDescription('Face Metrics Pro: AI facial analysis for personalised haircut and colour advice. First in Greece. €100 · 45 min at Ikonomakis Hair Atelier, Athens.');

    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    [
      { hreflang: 'en', href: PAGE_URL },
      { hreflang: 'el', href: `${SITE_URL}/services/face-metrics-pro` },
      { hreflang: 'x-default', href: `${SITE_URL}/services/face-metrics-pro` },
    ].forEach(({ hreflang, href }) => {
      const link = document.createElement('link'); link.rel = 'alternate'; link.hreflang = hreflang; link.href = href;
      document.head.appendChild(link);
    });

    const breadcrumb = {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home (EN)', item: `${SITE_URL}/en` },
        { '@type': 'ListItem', position: 2, name: 'AI Hair Analysis Athens', item: PAGE_URL },
      ],
    };

    const serviceSchema = {
      '@context': 'https://schema.org', '@type': 'Service',
      name: 'Face Metrics Pro — AI Hair Analysis',
      description: DEFINITIVE_STATEMENT + ' The service uses facial morphology analysis to recommend personalised haircut shape, colour technique and styling.',
      url: PAGE_URL,
      provider: { '@id': 'https://ikonomakis.gr/#salon' },
      offers: { '@type': 'Offer', priceCurrency: 'EUR', price: '100', priceSpecification: { '@type': 'PriceSpecification', minPrice: '100', priceCurrency: 'EUR' } },
      areaServed: [{ '@type': 'City', name: 'Athens' }, { '@type': 'Country', name: 'Greece' }],
    };



    clearJsonLd(); getSiteSchemas().forEach(addJsonLd);
    addJsonLd(HAIRSALON_SCHEMA); addJsonLd(breadcrumb); addJsonLd(serviceSchema);
  }, []);

  return (
    <div className="bg-cream-bg text-warm-black min-h-screen">
      <TopBar />
      <Nav />

      <div className="bg-warm-black text-cream text-tiny px-[6vw] py-2 flex justify-between flex-wrap gap-2">
        <span>🇬🇧 English · <a href="/services/face-metrics-pro" className="text-gold-light underline">Ελληνικά →</a></span>
        <span>First in Greece · AI Facial Analysis</span>
      </div>

      <nav aria-label="Breadcrumb" className="px-[6vw] py-3 bg-cream-soft/40 border-b border-brown/10">
        <div className="max-w-container mx-auto text-tiny text-muted/80 flex gap-2">
          <a href="/en" className="hover:text-warm-black">Home (EN)</a>
          <span>/</span>
          <span className="text-warm-black">AI Hair Analysis Athens</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="px-[6vw] pt-[80px] pb-[70px] bg-cream border-b border-brown/10">
        <div className="max-w-editorial mx-auto">
          <EyebrowPill variant="dark" className="mb-6">Face Metrics Pro · First in Greece · Since 2019</EyebrowPill>
          <h1 className="font-serif text-display-l text-warm-black font-medium tracking-[-2px] leading-[0.95] mb-6">
            AI Hair Analysis<br />
            <em className="italic text-gold">in Athens.</em>
          </h1>
          {/* DEFINITIVE QUOTABLE STATEMENT */}
          <p className="text-body-l text-warm-black font-medium leading-[1.65] mb-4 max-w-reading border-l-4 border-gold pl-5">
            {DEFINITIVE_STATEMENT}
          </p>
          <p className="text-body-l text-muted leading-[1.75] mb-8 max-w-reading">
            Face Metrics Pro is a structured facial analysis developed by Thodoris Ikonomakis — founder and master stylist at Ikonomakis Hair Atelier. In a 45-minute session (available in English), your facial geometry, proportions and symmetry are measured and documented. What you walk away with is a precise, written set of recommendations: cut shape, colour technique, fringe, length, styling — specific to your face.
          </p>
          <div className="flex flex-wrap gap-4 items-center">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-4 rounded-pill bg-warm-black text-gold-light font-semibold text-body-s hover:bg-gold hover:text-warm-black transition-all duration-300">
              Book Face Metrics Pro — €100 →
            </a>
            <div className="flex items-center gap-3 px-5 py-3 rounded-pill bg-cream-soft border border-brown/15">
              <span className="text-gold text-lg">✦</span>
              <span className="text-body-s font-semibold text-warm-black">45 minutes · English or Greek</span>
            </div>
          </div>
        </div>
      </section>

      {/* KEY FACTS */}
      <section className="px-[6vw] py-[60px]">
        <div className="max-w-container-narrow mx-auto">
          <KeyFactsBox lang="en" />
        </div>
      </section>

      {/* WHAT IT IS */}
      <section className="px-[6vw] py-[80px] bg-cream border-t border-brown/10">
        <div className="max-w-editorial mx-auto">
          <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] leading-[1.05] mb-6">
            What is Face Metrics Pro?
          </h2>
          <div className="space-y-4 text-body-l text-muted leading-[1.75] max-w-reading">
            <p>
              Most salon consultations go something like this: the stylist looks at your hair, you show them some photos, you talk about what you're after, and they use their judgement. It works well enough — until it doesn't. And when it doesn't, the problem usually isn't the execution. It's that the wrong cut was chosen in the first place.
            </p>
            <p>
              Face Metrics Pro adds a measurable layer to that process. Before any creative decisions are made, <strong className="text-warm-black">specific facial parameters are recorded</strong>: width-to-length ratio, forehead-to-jaw proportion, cheekbone position, jaw angle, left-right symmetry. These are mapped against a classification framework built by Thodoris Ikonomakis over 30 years of practice and training across 19 countries.
            </p>
            <p>
              The result isn't a mood board or a general direction. It's a <strong className="text-warm-black">documented, specific recommendation</strong> — the cut shape and length that will actually work with your proportions, the colour technique that suits your facial structure, the styling approach that plays to your features rather than fighting them.
            </p>
            <p>
              It helps to separate two ideas. <strong className="text-warm-black">Face metrics (AI measurement)</strong> is what Face Metrics Pro does: it objectively measures proportions, symmetry, face shape and ratios. <strong className="text-warm-black">Visagism (personal image design)</strong> is the human, interpretive part: the stylist reads the lines and expression of your face, your temperament and the message you want your image to send, then designs the look with you. Face Metrics Pro measures; the stylist interprets.
            </p>
            <p>
              <strong className="text-warm-black">€100 · 45 minutes · Greek or English.</strong> You receive a written report at the end. Use it here, share it with any stylist anywhere — it's yours.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-[6vw] py-[80px]">
        <div className="max-w-container-narrow mx-auto">
          <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] leading-[1.05] mb-10">
            How it works —<br /><em className="italic text-gold">step by step.</em>
          </h2>
          <div className="space-y-8">
            {PROCESS_STEPS.map(s => (
              <div key={s.n} className="flex gap-6 pb-8 border-b border-brown/10 last:border-0">
                <span className="font-serif text-display-s text-gold/30 font-medium leading-none shrink-0">{s.n}</span>
                <div>
                  <h3 className="font-serif text-h3 text-warm-black font-medium mb-2">{s.title}</h3>
                  <p className="text-body text-muted leading-[1.7]">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="px-[6vw] py-[80px] bg-cream border-t border-brown/10">
        <div className="max-w-container-narrow mx-auto">
          <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] leading-[1.05] mb-8">
            Real results —<br /><em className="italic text-gold">client cases.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { profile: 'Client A — Face shape: Elongated', challenge: 'Long face with a narrow forehead and prominent jawline. Previous cuts had added length, making features appear harsh.', result: 'Recommended: textured bob at jawline, soft fringe, babylights to add width visually. Result: face appears shorter, more balanced. Client rating: 5/5.' },
              { profile: 'Client B — Face shape: Round', challenge: 'Full cheeks and a soft, circular silhouette. Previous styles had inadvertently widened the face further.', result: 'Recommended: collarbone-length cut with layers, deep side part, balayage with lighter vertical streaks. Result: significant elongation effect. Client rating: 5/5.' },
              { profile: 'Client C — Face shape: Diamond', challenge: 'Pronounced cheekbones with a narrow forehead and chin. Needed visual width at forehead and chin, not cheekbones.', result: 'Recommended: light fringe, chin-length graduated bob, warm babylights framing the face. Result: proportions appear more harmonious. Client rating: 5/5.' },
            ].map(c => (
              <div key={c.profile} className="bg-cream-bg border border-brown/10 rounded-card p-6 flex flex-col gap-4">
                <h3 className="font-serif text-h3 text-warm-black font-medium">{c.profile}</h3>
                <div>
                  <p className="text-tiny font-semibold uppercase tracking-wide text-muted mb-1">Challenge</p>
                  <p className="text-body-s text-muted leading-[1.65]">{c.challenge}</p>
                </div>
                <div>
                  <p className="text-tiny font-semibold uppercase tracking-wide text-gold mb-1">Result</p>
                  <p className="text-body-s text-muted leading-[1.65]">{c.result}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section className="px-[6vw] py-[80px]">
        <div className="max-w-editorial mx-auto">
          <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] leading-[1.05] mb-6">
            Who is Face Metrics Pro for?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-reading">
            {[
              'First-time clients who want to make a confident decision before committing to a major change',
              'Anyone who\'s had a string of haircuts that were fine on paper but just didn\'t work',
              'International clients who want a clear, written recommendation they can use anywhere',
              'Anyone preparing for a wedding, an event, or a significant change in their appearance',
              'Clients who want a documented baseline for every future stylist they see',
              'People who\'ve always suspected their hair issues were about structure, not skill',
            ].map(item => (
              <div key={item} className="flex gap-3 items-start">
                <span className="text-gold text-lg shrink-0">✦</span>
                <p className="text-body text-muted leading-[1.65]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-[6vw] py-[80px] bg-cream-soft border-t border-brown/10">
        <div className="max-w-editorial mx-auto">
          <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] leading-[1.05] mb-10">
            Frequently asked questions.
          </h2>
          <DynamicServiceFAQ path="/en/ai-hair-analysis" fallback={FAQS} />
        </div>
      </section>

      {/* LINKS */}
      <section className="px-[6vw] py-[50px] border-t border-brown/10">
        <div className="max-w-container-narrow mx-auto">
          <h3 className="font-serif text-h3 text-warm-black font-medium mb-4">Related pages</h3>
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'English-Speaking Salon Athens', href: '/en/english-speaking-hair-salon-athens' },
              { label: 'Balayage in Athens', href: '/balayage-chalandri' },
              { label: 'Haircuts Athens', href: '/services/haircuts' },
              { label: 'Face Metrics Pro (Greek)', href: '/services/face-metrics-pro' },
              { label: 'Journal: AI Hair Analysis', href: '/ai-hair-analysis-greece' },
              { label: 'About Thodoris Ikonomakis', href: '/thodoris-ikonomakis-hair-expert' },
            ].map(l => (
              <a key={l.href} href={l.href} className="px-5 py-2.5 rounded-pill border border-brown/20 text-body-s text-warm-black font-medium hover:bg-warm-black hover:text-gold-light hover:border-warm-black transition-all duration-200">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="px-[6vw] pb-[100px] pt-[40px]">
        <FinalCTACard
          title="Ready to find your"
          titleEm="perfect haircut?"
          description="Book a Face Metrics Pro session — €100 · 45 min · English spoken · Chalandri, Athens"
          ctaText="Book Face Metrics Pro →"
          ctaHref={BOOKING_URL}
          ctaTarget="_blank"
          ctaRel="noopener noreferrer"
        />
      </section>

      <Footer />
    </div>
  );
}